import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { appConfig } from "../config/app.confing";
import {
	ZoomIn,
	ZoomOut,
	Maximize,
	Minimize,
	Sun,
	Moon,
	RefreshCw,
	AlignLeft,
	AlignCenter,
	AlignRight,
	Accessibility as AccessibilityIcon,
	Circle,
	Eye,
	Settings,
	Volume2,
	User,
	Brain,
	Monitor,
	ALargeSmall,
	BetweenHorizonalStart,
	Droplet,
	Droplets,
	DropletOff,
	PersonStanding,
	AudioLines,
	Contrast,
	Ligature,
	ScanLine,
	Scan,
} from "lucide-react";
export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export const processSvgForColoring = (svgContent, color, size) => {
	const tempDiv = document.createElement("div");
	tempDiv.innerHTML = svgContent;
	const svgElement = tempDiv.querySelector("svg");

	if (svgElement) {
		const uniqueId = `svg-${Date.now()}-${Math.random()
			.toString(36)
			.substr(2, 9)}`;
		svgElement.setAttribute("id", uniqueId);
		svgElement.setAttribute("width", size.toString());
		svgElement.setAttribute("height", size.toString());

		if (!svgElement.getAttribute("viewBox")) {
			const originalWidth = svgElement.getAttribute("width") || "24";
			const originalHeight = svgElement.getAttribute("height") || "24";
			svgElement.setAttribute(
				"viewBox",
				`0 0 ${originalWidth} ${originalHeight}`,
			);
		}

		// Check if SVG uses currentColor or stroke-based styling
		const hasCurrentColor = svgContent.includes("currentColor");
		const hasStrokeOnly =
			svgContent.includes('fill="none"') && svgContent.includes("stroke");
		const isLucideIcon = svgContent.includes("lucide");

		// Handle different SVG types differently
		if (hasCurrentColor || hasStrokeOnly || isLucideIcon) {
			// For SVGs that use currentColor or are stroke-based (like Lucide icons)
			// Just set the color and size without removing attributes
			svgElement.style.color = color;
			svgElement.style.width = `${size}px`;
			svgElement.style.height = `${size}px`;

			// Add CSS to ensure proper coloring
			const style = document.createElement("style");
			style.textContent = `
				#${uniqueId} {
					width: ${size}px !important;
					height: ${size}px !important;
					display: block;
					color: ${color};
				}
				#${uniqueId}[stroke="currentColor"], 
				#${uniqueId} *[stroke="currentColor"] {
					stroke: ${color};
				}
				#${uniqueId}[fill="currentColor"], 
				#${uniqueId} *[fill="currentColor"] {
					fill: ${color};
				}
			`;
			svgElement.insertBefore(style, svgElement.firstChild);
		} else {
			// For other SVGs, use the original aggressive approach
			const elementsToColor = svgElement.querySelectorAll("*");

			elementsToColor.forEach((el) => {
				const currentFill = el.getAttribute("fill");
				if (
					currentFill &&
					currentFill !== "none" &&
					currentFill !== "transparent" &&
					currentFill !== "currentColor"
				) {
					el.removeAttribute("fill");
				}

				const currentStroke = el.getAttribute("stroke");
				if (
					currentStroke &&
					currentStroke !== "none" &&
					currentStroke !== "transparent" &&
					currentStroke !== "currentColor"
				) {
					el.removeAttribute("stroke");
				}

				const style = el.getAttribute("style");
				if (style) {
					const cleaned = style
						.replace(
							/fill\s*:\s*(?!currentColor|none|transparent)[^;]+;?/gi,
							"",
						)
						.replace(
							/stroke\s*:\s*(?!currentColor|none|transparent)[^;]+;?/gi,
							"",
						)
						.replace(/width\s*:\s*[^;]+;?/gi, "")
						.replace(/height\s*:\s*[^;]+;?/gi, "")
						.trim();
					if (cleaned) el.setAttribute("style", cleaned);
					else el.removeAttribute("style");
				}
			});

			const style = document.createElement("style");
			style.textContent = `
				#${uniqueId} {
					width: ${size}px !important;
					height: ${size}px !important;
					display: block;
				}
				#${uniqueId} * {
					fill: ${color};
					color: ${color};
				}
			`;
			svgElement.insertBefore(style, svgElement.firstChild);
			svgElement.setAttribute("fill", "currentColor");
			svgElement.style.color = color;
			svgElement.style.width = `${size}px`;
			svgElement.style.height = `${size}px`;
		}

		return tempDiv.innerHTML;
	}

	return svgContent;
};
export const getInitialColor = () => {
	const defaultColor = appConfig.iconPrimaryColor;
	if (typeof window === "undefined") return defaultColor;

	try {
		// Prefer unified svgSettings storage (new) then fall back to legacy key
		let savedColor = null;
		const raw = localStorage.getItem("svgSettings");
		if (raw) {
			try {
				const parsed = JSON.parse(raw);
				savedColor = parsed?.bgColor?.color || parsed?.backgroundColor || null;
			} catch {
				/* ignore parse error */
			}
		}
		if (!savedColor) {
			// Legacy single key fallback
			savedColor = localStorage.getItem("primary-icon-color");
		}
		const final = savedColor || defaultColor;
		document.documentElement.style.setProperty("--primary-icon", final);
		return final;
	} catch {
		return defaultColor;
	}
};

export const getInitialButtonPosition = () => {
	const defaultPosition = appConfig.svgSettings.buttonPosition;
	if (typeof window === "undefined") return defaultPosition;

	try {
		let saved = null;
		const raw = localStorage.getItem("svgSettings");
		if (raw) {
			try {
				const parsed = JSON.parse(raw);
				saved = parsed?.buttonPosition || null;
			} catch {
				/* ignore */
			}
		}
		if (!saved) {
			// Legacy key
			saved = localStorage.getItem("button-position-desktop");
		}
		return saved || defaultPosition;
	} catch {
		return defaultPosition;
	}
};
export const getInitialButtonPositionMobile = () => {
	const defaultPosition = appConfig.svgSettings.buttonPositionMobile;
	if (typeof window === "undefined") return defaultPosition;

	try {
		let saved = null;
		const raw = localStorage.getItem("svgSettings");
		if (raw) {
			try {
				const parsed = JSON.parse(raw);
				saved = parsed?.buttonPositionMobile || null;
			} catch {
				/* ignore */
			}
		}
		if (!saved) {
			saved = localStorage.getItem("button-position-mobile");
		}
		return saved || defaultPosition;
	} catch {
		return defaultPosition;
	}
};
export function hexToRgb(hex) {
	if (!hex || typeof hex !== "string") return { r: 0, g: 0, b: 0 }; // Fallback to black

	hex = hex.replace("#", "");

	if (hex.length === 3) {
		hex = hex
			.split("")
			.map((char) => char + char)
			.join("");
	}

	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	return { r, g, b };
}

export function getContrastTextColor(hexColor) {
	const { r, g, b } = hexToRgb(hexColor);
	// Calculate luminance (YIQ method for perceived brightness)
	// A common threshold for switching between black and white text is around 128.
	const yiq = (r * 299 + g * 587 + b * 114) / 1000;
	return yiq >= 128 ? "#000000" : "#FFFFFF";
}
// export const stepBasedFeatures = {
// 	oversizedView: 4,
// 	contrast: 3,
// };
export const stepBasedFeatures = {
	contentScaling: {
		steps: 4,
		mode: "click",
		labels: [
			"Content Size 1.1x",
			"Content Size 1.2x",
			"Content Size 1.3x",
			"Content Size 1.4x",
		],
		icons: [ZoomIn, ZoomIn, ZoomIn, ZoomIn], // example icons per step
	},
	contrast: {
		steps: 3,
		mode: "click",
		labels: ["Dark Contrast", "Light Contrast", "High Contrast"],
		icons: [Sun, ZoomIn, Moon],
	},
	contentAlignment: {
		steps: 3,
		mode: "click",
		labels: ["Align Left", "Align Center", "Align Right"],
		icons: [AlignLeft, AlignCenter, AlignRight],
	},
	colorSaturation: {
		steps: 3,
		mode: "click",
		labels: ["Low Saturation", "High Saturation", "Desaturate"],
		icons: [Droplet, Droplets, DropletOff],
	},
	adjustLineHeight: {
		steps: 3,
		mode: "click",
		labels: ["Line Height 1.5x", "Line Height 1.75x", "Line Height 2x"],
		icons: [
			BetweenHorizonalStart,
			BetweenHorizonalStart,
			BetweenHorizonalStart,
		],
	},
	adjustLetterSpacing: {
		steps: 3,
		mode: "click",
		labels: ["Spacing 1.25x", "Spacing 1.5x", "Spacing 1.75x"],
	},
	adjustFontSizing: {
		steps: 3,
		mode: "click",
		labels: ["Text Size 1.25x", "Text Size 1.50x", "Text Size 1.75x"],
		icons: [ALargeSmall, ALargeSmall, ALargeSmall],
	},
	bigCursor: {
		steps: 3, // sm, md, lg (then reset)
		mode: "click",
		labels: ["Cursor Small", "Cursor Medium", "Cursor Large"],
		// reuse ALargeSmall icon or could add custom icons per step
		//icons: [ALargeSmall, ALargeSmall, ALargeSmall],
	},
	pointerTrail: {
		steps: 6, // trail, neon, particle, rainbow, geometric, line (then reset)
		mode: "click",
		labels: ["Trail", "Neon", "Particle", "Rainbow", "Geometric", "Line"],
	},
};
export const stepLabels = {
	contentScaling: ["Zoom 1.1x", "Zoom 1.2x", "Zoom 1.3x", "Zoom 1.4x"],
	contrast: ["Default", "Bright", "Dark", "Reset"],
};
export const accessibilityProfile = [
	{
		id: "motor-impaired",
		title: "Motor Impaired",
		icon: PersonStanding,
		features: [
			{ key: "tooltips" },
			//{ key: "contentScaling", step: 0 }, // Zoom 1.1x
		],
	},
	{
		id: "blind-users",
		title: "Blind Users",
		icon: AudioLines,
		features: [{ key: "screenReader" }],
	},
	{
		id: "color-blind",
		title: "Color Blind",
		icon: Contrast,
		features: [
			{ key: "readableFont" }, // Bright theme variant
			{ key: "colorSaturation", step: 1 },
		],
	},
	{
		id: "dyslexia",
		title: "Dyslexia",
		icon: Ligature,
		features: [{ key: "dyslexiaFriendly" }],
	},
	{
		id: "low-vision",
		title: "Low vision",
		icon: Eye,
		features: [
			{ key: "contentScaling", step: 0 },
			{ key: "readableFont" },
			{ key: "bigCursor" },
			{ key: "tooltips" },
			{ key: "colorSaturation", step: 1 },
		],
	},
	{
		id: "cognitive-learning",
		title: "Cognitive Learning",
		icon: ScanLine,
		features: [
			{ key: "readingMask" },
			{ key: "adjustFontSizing", step: 0 },
			{ key: "tooltips" },
		],
	},
	{
		id: "seizure-epileptic",
		title: "Seizure & Epileptic",
		icon: Settings,
		features: [{ key: "colorSaturation", step: 0 }],
	},
	{
		id: "adhd-friendly",
		title: "ADHD  Profile",
		icon: Scan,
		features: [{ key: "readingMask" }, { key: "colorSaturation", step: 0 }],
	},
];
